import { api } from "./baseApi";

export interface AuditLog {
  id: string;
  userId?: string | null;
  action: string;
  entityType?: string | null;
  entityId?: string | null;
  oldValues?: any;
  newValues?: any;
  ipAddress?: string | null;
  userAgent?: string | null;
  outletId?: string | null;
  storeId?: string | null;
  metadata?: any;
  createdAt: string;
  user?: {
    id: string;
    name: string;
    username: string;
  } | null;
  // Computed/derived properties used by UI components
  timestamp?: string; // Alias for createdAt or formatted timestamp
  username?: string; // Derived from user.username
  userRole?: string; // User role if available
  outletName?: string; // Outlet name if available
  changes?: any; // Changes object combining oldValues and newValues
}

export interface AuditSummary {
  period: string;
  startDate: string;
  endDate: string;
  summary: {
    totalActions: number;
    byAction: Array<{ action: string; count: number }>;
    byEntityType: Array<{ entityType: string; count: number }>;
    mostActiveUsers: Array<{
      user: { id: string; name: string; username: string };
      activityCount: number;
    }>;
  };
}

export interface AuditQueryOptions {
  page?: number;
  limit?: number;
  userId?: string;
  username?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  outletId?: string;
  outletIds?: string[];
  storeId?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: "createdAt" | "action" | "entityType";
  sortOrder?: "asc" | "desc";
}

export const auditApi = api.injectEndpoints({
  endpoints: builder => ({
    // Get audit logs
    getAuditLogs: builder.query<
      {
        logs: AuditLog[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      },
      AuditQueryOptions
    >({
      query: (params = {}) => ({
        url: "/audit/logs",
        params,
      }),
      providesTags: ["Audit"],
      transformResponse: (response: { success: boolean; data: any }) => response.data,
    }),

    // Get audit log by ID
    getAuditLogById: builder.query<AuditLog, string>({
      query: id => `/audit/logs/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Audit", id }],
      transformResponse: (response: { success: boolean; data: AuditLog }) => response.data,
    }),

    // Get audit logs for a specific outlet
    getOutletAuditLogs: builder.query<
      {
        logs: AuditLog[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      },
      { outletId: string } & Omit<AuditQueryOptions, 'outletId'>
    >({
      query: ({ outletId, ...params }) => ({
        url: `/audit/outlets/${outletId}/logs`,
        params,
      }),
      providesTags: (_result, _error, { outletId }) => [
        { type: "Audit", id: `outlet-${outletId}` },
      ],
      transformResponse: (response: { success: boolean; data: any }) => response.data,
    }),

    // Get audit logs for a specific store (all outlets)
    getStoreAuditLogs: builder.query<
      {
        logs: AuditLog[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      },
      { storeId: string } & Omit<AuditQueryOptions, 'storeId'>
    >({
      query: ({ storeId, ...params }) => ({
        url: `/audit/stores/${storeId}/logs`,
        params,
      }),
      providesTags: (_result, _error, { storeId }) => [
        { type: "Audit", id: `store-${storeId}` },
      ],
      transformResponse: (response: { success: boolean; data: any }) => response.data,
    }),

    // Get entity history
    getEntityHistory: builder.query<
      {
        entityType: string;
        entityId: string;
        history: AuditLog[];
      },
      { entityType: string; entityId: string; limit?: number }
    >({
      query: ({ entityType, entityId, limit = 20 }) => ({
        url: `/audit/entity/${entityType}/${entityId}`,
        params: { limit },
      }),
      providesTags: (_result, _error, { entityType, entityId }) => [
        { type: "Audit", id: `${entityType}-${entityId}` },
      ],
    }),

    // Get user activity
    getUserActivity: builder.query<
      {
        userId: string;
        logs: AuditLog[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      },
      {
        userId: string;
        page?: number;
        limit?: number;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: ({ userId, ...params }) => ({
        url: `/audit/user/${userId}`,
        params,
      }),
      providesTags: (_result, _error, { userId }) => [{ type: "Audit", id: `user-${userId}` }],
    }),

    // Get audit summary
    getAuditSummary: builder.query<AuditSummary, { period?: string; outletId?: string; storeId?: string }>({
      query: ({ period = "day", outletId, storeId } = {}) => ({
        url: "/audit/summary",
        params: { period, outletId, storeId },
      }),
      providesTags: ["AuditSummary"],
      transformResponse: (response: { success: boolean; data: AuditSummary }) => response.data,
    }),

    // Get available audit actions
    getAuditActions: builder.query<string[], void>({
      query: () => "/audit/actions",
      providesTags: ["AuditActions"],
      transformResponse: (response: { success: boolean; data: string[] }) => response.data,
    }),

    // Get available entity types
    getEntityTypes: builder.query<string[], void>({
      query: () => "/audit/entity-types",
      providesTags: ["EntityTypes"],
      transformResponse: (response: { success: boolean; data: string[] }) => response.data,
    }),

    // Export audit logs
    exportAuditLogs: builder.mutation<Blob, AuditQueryOptions & { format?: "json" | "csv" }>({
      query: params => ({
        url: "/audit/export",
        params,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // Clean old audit logs
    cleanOldAuditLogs: builder.mutation<
      { deletedCount: number; retentionDays: number },
      { retentionDays?: number }
    >({
      query: ({ retentionDays = 365 } = {}) => ({
        url: "/audit/clean",
        method: "DELETE",
        params: { retentionDays },
      }),
      invalidatesTags: ["Audit", "AuditSummary"],
    }),
  }),
});

export const {
  useGetAuditLogsQuery,
  useGetAuditLogByIdQuery,
  useGetOutletAuditLogsQuery,
  useGetStoreAuditLogsQuery,
  useGetEntityHistoryQuery,
  useGetUserActivityQuery,
  useGetAuditSummaryQuery,
  useGetAuditActionsQuery,
  useGetEntityTypesQuery,
  useExportAuditLogsMutation,
  useCleanOldAuditLogsMutation,
} = auditApi;
